import React from "react";

/**
 * motion — the Pulse reveal vocabulary.
 *
 * Six moves, one easing curve, and each card plays its choreography once as
 * it enters the viewport. Three rules, each of them a bug we already hit:
 *
 *  1. Every hidden-at-rest state lives inside `prefers-reduced-motion:
 *     no-preference`. Under `reduce` nothing is hidden and nothing animates,
 *     so the reader gets the FINISHED page rather than an empty one.
 *  2. Animations fill `backwards`, never `forwards`. A forwards fill outranks
 *     inline style in the cascade, so it would silently kill every hover state
 *     a panel sets on an element it also animated.
 *  3. The reveal has a safety net. An IntersectionObserver only reports a
 *     state CHANGE, so a card the reader jumps straight past — restored scroll
 *     position, an anchor, a webview that never fires IO — would stay at
 *     opacity 0 forever. A timeout reveals anything IO missed.
 */

export const PULSE_KEYFRAMES = `
@keyframes tgpRise{from{opacity:0;transform:translateY(8px)}}
@keyframes tgpFade{from{opacity:0}}
@keyframes tgpPop{from{opacity:0;transform:scale(.5)}}
@keyframes tgpGrowX{from{opacity:0;transform:scaleX(0)}}
@keyframes tgpGrowY{from{opacity:0;transform:scaleY(0)}}
@keyframes tgpDraw{from{stroke-dashoffset:var(--tgp-len,0)}}
@keyframes tgpPing{0%{opacity:0;transform:scale(1)}22%{opacity:.85}100%{opacity:0;transform:scale(2.1)}}

.tgp-x{transition:opacity .13s linear}
.tgp-lab{transition:fill .13s linear}

@media (prefers-reduced-motion:no-preference){
  .tgp:not(.tgp-in) .tgp-rise,
  .tgp:not(.tgp-in) .tgp-fade,
  .tgp:not(.tgp-in) .tgp-pop,
  .tgp:not(.tgp-in) .tgp-gx,
  .tgp:not(.tgp-in) .tgp-gy,
  .tgp:not(.tgp-in) .tgp-ping{opacity:0}
  .tgp:not(.tgp-in) .tgp-draw{stroke-dashoffset:var(--tgp-len,0)}

  .tgp.tgp-in .tgp-rise{animation:tgpRise .5s var(--tgp-ease) backwards;animation-delay:var(--tgp-d,0s)}
  .tgp.tgp-in .tgp-fade{animation:tgpFade .4s ease-out backwards;animation-delay:var(--tgp-d,0s)}
  .tgp.tgp-in .tgp-pop{animation:tgpPop .36s var(--tgp-ease) backwards;animation-delay:var(--tgp-d,0s);transform-box:fill-box;transform-origin:center}
  .tgp.tgp-in .tgp-gx{animation:tgpGrowX .48s var(--tgp-ease) backwards;animation-delay:var(--tgp-d,0s);transform-box:fill-box;transform-origin:var(--tgp-ox,left) center}
  .tgp.tgp-in .tgp-gy{animation:tgpGrowY .48s var(--tgp-ease) backwards;animation-delay:var(--tgp-d,0s);transform-box:fill-box;transform-origin:center bottom}
  .tgp.tgp-in .tgp-draw{animation:tgpDraw .72s var(--tgp-ease) backwards;animation-delay:var(--tgp-d,0s)}
  .tgp.tgp-in .tgp-ping{animation:tgpPing 1.15s var(--tgp-ease) backwards;animation-delay:var(--tgp-d,0s);transform-box:fill-box;transform-origin:center}

  /* A root that has already played must not replay when its contents
     re-render — a theme flip or a refetch rebuilds the whole subtree. */
  .tgp.tgp-settled .tgp-rise,.tgp.tgp-settled .tgp-fade,.tgp.tgp-settled .tgp-pop,
  .tgp.tgp-settled .tgp-gx,.tgp.tgp-settled .tgp-gy,.tgp.tgp-settled .tgp-draw,
  .tgp.tgp-settled .tgp-ping{animation:none !important}
}
`;

export function PulseMotion() {
  return <style>{PULSE_KEYFRAMES}</style>;
}

/**
 * Marks a subtree as a reveal root. Returns a ref to spread onto the card.
 * `settleAfter` is the belt-and-braces pass: once the choreography has run
 * once, later re-renders inside the same root skip it.
 */
export function useRevealRoot(deps = []) {
  const ref = React.useRef(null);
  const played = React.useRef(false);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    el.classList.add("tgp");
    el.style.setProperty("--tgp-ease", "cubic-bezier(.16,1,.3,1)");

    if (played.current) { el.classList.add("tgp-in", "tgp-settled"); return undefined; }

    let io = null;
    const reveal = () => {
      if (!ref.current) return;
      ref.current.classList.add("tgp-in");
      played.current = true;
      window.setTimeout(() => { if (ref.current) ref.current.classList.add("tgp-settled"); }, 2600);
    };

    if (typeof IntersectionObserver === "function") {
      io = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) { reveal(); if (io) io.disconnect(); }
      }, { threshold: 0.08, rootMargin: "0px 0px -40px 0px" });
      io.observe(el);
    } else {
      reveal();
    }
    // safety net — nothing may stay invisible because IO never fired
    const t = window.setTimeout(reveal, 1600);
    return () => { window.clearTimeout(t); if (io) io.disconnect(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return ref;
}

/** Tag a plain element with a move + delay. Returns props to spread. */
export const anim = (move, delay) => ({
  className: `tgp-${move}`,
  style: delay ? { "--tgp-d": `${(+delay).toFixed(3)}s` } : undefined,
});

/** Same, but merged into an existing style object (SVG nodes mostly). */
export const animStyle = (delay, extra) => ({
  ...(extra || {}),
  ...(delay ? { "--tgp-d": `${(+delay).toFixed(3)}s` } : {}),
});

/** Measure a path so `.tgp-draw` can draw it. Ref callback. */
export const drawRef = (delay) => (node) => {
  if (!node || typeof node.getTotalLength !== "function") return;
  let len = 0;
  try { len = node.getTotalLength(); } catch (_) { len = 0; }
  len = Math.ceil(len) || 1;
  node.style.setProperty("--tgp-len", `${len}px`);
  node.style.strokeDasharray = String(len);
  if (delay) node.style.setProperty("--tgp-d", `${(+delay).toFixed(3)}s`);
};

/**
 * A figure that counts up the first time it is shown. Falls straight to the
 * final value under reduced motion, and whenever the value changes after the
 * first run (a range change must read as new data, not as an animation).
 */
export function CountUp({ value, format, duration = 900 }) {
  const fmt = format || ((x) => Math.round(x).toLocaleString());
  const [shown, setShown] = React.useState(() => value);
  const first = React.useRef(true);

  React.useEffect(() => {
    const reduced = typeof window !== "undefined" && window.matchMedia
      && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced || !first.current || !Number.isFinite(value)) { setShown(value); first.current = false; return undefined; }
    first.current = false;
    let raf = 0;
    const t0 = (typeof performance !== "undefined" ? performance.now() : Date.now());
    const step = (ts) => {
      const p = Math.min(1, ((ts || Date.now()) - t0) / duration);
      setShown(value * (1 - Math.pow(1 - p, 3)));
      if (p < 1) raf = window.requestAnimationFrame(step);
    };
    raf = window.requestAnimationFrame(step);
    return () => window.cancelAnimationFrame(raf);
  }, [value, duration]);

  return <>{fmt(shown)}</>;
}
